import { useCallback, useState, useRef } from 'react';
import { useTranslation } from '../../../../../utils/useTranslation';
import { IconKebab } from '../../../../icon/IconKebab';
import { Button } from '../../../../input/Button';
import { Checkbox } from '../../../../input/Checkbox';
import { Flex } from '../../../../layout/Flex';
import { Menu, MenuItem } from '../../../../navigation/Menu';
import { Typography } from '../../../../typography/Typography';
import { FeedManagementMenuProps } from './types';
import type { MenuRef } from '../../../../navigation/Menu/types';

export function FeedManagementMenu<TValue>(props: FeedManagementMenuProps<TValue>) {
    const { options, values = [], onChange, dataTestId, ...other } = props;
    const t = useTranslation();
    const [selected, setSelected] = useState<TValue[]>(values);
    const menuRef = useRef<MenuRef>(null);

    const onItemChange = useCallback(
        (value: TValue) => {
            if (selected.indexOf(value) === -1) {
                setSelected([...selected, value]);

                return;
            }

            setSelected(selected.filter((item) => item !== value));
        },
        [selected],
    );

    const onSubmit = useCallback(() => {
        onChange(selected);
        menuRef.current?.close();
    }, [onChange, selected]);

    return (
        <Menu
            ref={menuRef}
            {...other}
            trigger={
                <Button width="32px" size="s" color="secondary" dataTestId={dataTestId}>
                    <IconKebab size={16} color="var(--color-primary)" />
                </Button>
            }
        >
            {options
                .map((option) => (
                    <MenuItem
                        key={option.displayName}
                        direction="row"
                        justifyContent="left"
                        spacing={1}
                        onClick={() => onItemChange(option.value)}
                        disableClose
                    >
                        <Checkbox
                            checked={selected.indexOf(option.value) !== -1}
                            onChange={() => onItemChange(option.value)}
                        />
                        <Typography variant="body" noWrap>
                            {option.displayName}
                        </Typography>
                    </MenuItem>
                ))
                .concat(
                    <Flex
                        key="save-button"
                        spacing={1}
                        direction="row"
                        width="100%"
                        py={1.5}
                        px={2}
                    >
                        <Button width="100%" color="secondary" onClick={onSubmit}>
                            {t('Применить')}
                        </Button>
                    </Flex>,
                )}
        </Menu>
    );
}
